/**
 * Pure staging logic for the AI Import upload step.
 *
 * Kept out of the component so the rules that decide "can this batch proceed?"
 * and "what still needs uploading?" are unit-testable. The component owns React
 * state and the network; this file owns the arithmetic.
 */

export type FileState = 'queued' | 'uploading' | 'uploaded' | 'failed';

/** Why a row is in the `failed` state — the two kinds recover differently. */
export type FailureKind =
  /** Rejected before any upload: wrong type, too big, over the count cap. The
   *  file can never succeed, so the user has to remove it. */
  | 'rejected'
  /** The upload itself failed (network, throttling, a 5xx). Worth another go. */
  | 'upload';

export interface StagedFileLike {
  id: string;
  state: FileState;
  failureKind?: FailureKind;
  sharepoint_item_id?: string;
}

/**
 * Files still needing an upload attempt.
 *
 * Anything already carrying a SharePoint item id is skipped: re-submitting used
 * to re-push every byte into a brand-new folder, so a retry after one bad file
 * re-uploaded the whole batch and orphaned the first copy.
 */
export function filesNeedingUpload<T extends StagedFileLike>(files: T[]): T[] {
  return files.filter((f) => !(f.state === 'uploaded' && f.sharepoint_item_id));
}

/**
 * Rows the user must remove before the batch can go anywhere. Upload failures
 * are deliberately excluded — those are retryable, and treating them as
 * blocking is what turned one dropped connection into a dead-end modal.
 */
export function blockingFiles<T extends StagedFileLike>(files: T[]): T[] {
  return files.filter((f) => f.state === 'failed' && f.failureKind !== 'upload');
}

/** Rows a retry would pick back up. */
export function retriableFiles<T extends StagedFileLike>(files: T[]): T[] {
  return files.filter((f) => f.state === 'failed' && f.failureKind === 'upload');
}

export interface QueueCounts {
  total: number;
  uploaded: number;
  queued: number;
  failed: number;
  /** Of `failed`, how many are worth retrying. */
  retriable: number;
  /** Of `failed`, how many must be removed. */
  blocking: number;
}

export function countQueue(files: StagedFileLike[]): QueueCounts {
  const failed = files.filter((f) => f.state === 'failed');
  return {
    total: files.length,
    uploaded: files.filter((f) => f.state === 'uploaded').length,
    queued: files.filter((f) => f.state === 'queued' || f.state === 'uploading').length,
    failed: failed.length,
    retriable: failed.filter((f) => f.failureKind === 'upload').length,
    blocking: failed.filter((f) => f.failureKind !== 'upload').length,
  };
}

/**
 * Can the submit button fire? Blocked only by rows that can never succeed —
 * a retriable upload failure leaves the button live so the click retries it.
 */
export function canSubmit(counts: QueueCounts): boolean {
  return counts.total > 0 && counts.blocking === 0;
}

/** What the submit button should say for the current queue. */
export function submitLabel(counts: QueueCounts): string {
  if (counts.retriable > 0) {
    return `Retry ${counts.retriable} file${counts.retriable === 1 ? '' : 's'}`;
  }
  if (counts.uploaded === counts.total && counts.total > 0) return 'Start analysis';
  return 'Upload & analyze';
}

/**
 * Footer status line. The old one always read "N files ready", which flatly
 * contradicted the row badges after a failure and made the modal look frozen.
 */
export function queueSummary(counts: QueueCounts, sizeLabel: string): string {
  if (counts.total === 0) return 'Add at least one file to continue.';

  const parts: string[] = [];
  if (counts.uploaded) parts.push(`${counts.uploaded} uploaded`);
  if (counts.queued) parts.push(`${counts.queued} queued`);
  if (counts.failed) parts.push(`${counts.failed} failed`);

  const state = parts.length ? parts.join(' · ') : `${counts.total} ready`;
  const suffix = counts.blocking
    ? ' — remove the files that can’t be uploaded to continue'
    : counts.retriable
      ? ' — retry to finish the upload'
      : '';
  return `${state} · ${sizeLabel}${suffix}`;
}
